import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StateService } from '@uirouter/angular';
import { ToastrService } from 'ngx-toastr';
import { DuplicateItemActions } from '../enums/duplicate-item-actions.enum';
import { QuestionnaireMasterAvailability } from '../enums/questionnaire-master-availability.enum';
import { QuestionnaireMasterStatus } from '../enums/questionnaire-master-status.enum';
import { DuplicateItemQfmSections } from '../modals/edl-modal-duplicate-item/models/duplicate-item-qfm-sections.model';
import { DuplicateItemQfm } from '../modals/edl-modal-duplicate-item/models/duplicate-item-qfm.model';
import { DuplicateItemFormQfms } from '../modals/edl-modal-duplicate-item/models/duplicate-item-form-qfms.model';
import { DuplicateItemForm } from '../modals/edl-modal-duplicate-item/models/duplicate-item-form.model';
import { ModalService } from '../modals/modal.service';
import { ArchiveWarnings } from '../model/archive-warnings.model';
import { CplRequestBasicData } from '../model/cpl-request-basic-data.model';
import { CplRequestDuplicateData } from '../model/cpl-request-duplicate-data.model';
import { CplRequestQfm } from '../model/cpl-request-qfm.model';
import { CplRequestSummaryItem } from '../model/cpl-request-summary-item.model';
import { RequestItemService } from '../services/request-item.service';
import { FunctionalityNAModalComponent } from '../supervision-management/compliance-items/compliance-item/modals/function-na-modal.component';
import { QuestionnaireService } from './questionnaire.service';
import { COURSE_ACTIVITY_NAME_MAX_LENGTH } from '../constants/a48.constants';
import { SessionService } from '@edl-development/edl-common';


@Injectable()
export class RequestItemActionService {
    constructor(
        private requestItemService: RequestItemService,
        private ngbModal: NgbModal,
        private modalService: ModalService,
        private toastr: ToastrService,
        private stateService: StateService,
        private sessionService: SessionService,
        private questionnaireService: QuestionnaireService
    ) {}

    convertToTemplate(basicData: CplRequestBasicData) {
        this.questionnaireService.getQuestionnairesForRequest(basicData.RequestID)
            .subscribe(({ questionnaires, followupQuestionnaires } : { questionnaires: CplRequestQfm[], followupQuestionnaires: CplRequestQfm[] }) => {
                const questinonaireSections = this.generateQuestionnaireSections(questionnaires, followupQuestionnaires);

                this.modalService
                    .duplicateItem({
                        modalTitle: 'Convert Request Item To Template',
                        itemTitle: basicData.Title,
                        maxTitleLength: COURSE_ACTIVITY_NAME_MAX_LENGTH,
                        questionnaireSections: questinonaireSections,
                        status: QuestionnaireMasterStatus.TEMPLATE,
                        availability: QuestionnaireMasterAvailability.REQUEST_ITEMS,
                        isTemplate: false,
                        toTemplate: true
                    })
                    .then((results: DuplicateItemForm) => {
                        const data = this.generateCplRequestDuplicateData([questionnaires, followupQuestionnaires], basicData.RequestID, basicData.PortalID, results);

                        this.requestItemService
                            .convertToTemplate(data)
                            .subscribe((response: CplRequestSummaryItem) => {
                                this.toastr.success('Request item successfully converted to Template.');
                                this.stateService.go('authenticated.requests.sm.request.edit', {
                                    requestId: response.RequestID,
                                    page: 1
                                },{
                                    reload: true
                                });
                            });
                    }, () => {});
            });
    }

    duplicate(basicData: CplRequestBasicData, isTemplate: boolean){
        if(!this.sessionService.PortalSettings.ComplyEnabled && isTemplate){
            let modalRef = this.ngbModal.open(FunctionalityNAModalComponent, {
                size: 'lg',
                backdrop: 'static'
            });
        } else {
            this.performDuplicate(basicData, isTemplate);
        }
    }

    private performDuplicate(basicData: CplRequestBasicData, isTemplate: boolean){
        this.questionnaireService.getQuestionnairesForRequest(basicData.RequestID)
            .subscribe(({ questionnaires, followupQuestionnaires } : { questionnaires: CplRequestQfm[], followupQuestionnaires: CplRequestQfm[] }) => {
                const questinonaireSections = this.generateQuestionnaireSections(questionnaires, followupQuestionnaires);

                this.modalService
                    .duplicateItem({
                        modalTitle: 'Duplicating Request Item',
                        itemTitle: basicData.Title,
                        maxTitleLength: COURSE_ACTIVITY_NAME_MAX_LENGTH,
                        questionnaireSections: questinonaireSections,
                        status: QuestionnaireMasterStatus.PUBLISHED,
                        availability: QuestionnaireMasterAvailability.REQUEST_ITEMS,
                        isTemplate: isTemplate,
                        toTemplate: false
                    })
                    .then((results: DuplicateItemForm) => {
                        const data = this.generateCplRequestDuplicateData([questionnaires, followupQuestionnaires], basicData.RequestID, basicData.PortalID, results);

                        this.requestItemService
                            .duplicateItem(data)
                            .subscribe((response: CplRequestSummaryItem) => {
                                this.toastr.success('Request item successfully duplicated.');
                                this.stateService.go('authenticated.requests.sm.request.edit', {
                                    requestId: response.RequestID,
                                    page: 1
                                },{
                                    reload: true
                                });
                            });
                    }, () => {});
            });
    }

    archive(request: CplRequestBasicData){
        this.requestItemService.getArchiveWarnings(request.RequestID)
            .subscribe((response: ArchiveWarnings) => {    
                this.modalService
                .archvieItem('Confirm', 'Are you sure you want to archive this request item?', response)
                .then(() => {
                    this.requestItemService
                        .archiveItem(request)
                        .subscribe((response) => {
                            this.toastr.success('Request Item successfully archived.');
                            this.refreshList();
                        });
                }, () => {});
            });
    }

    delete(request: CplRequestBasicData){
        this.modalService
            .confirm('Confirm', 'Are you sure you want to delete this request item? <br/><br/><strong>Please consider archiving instead. </strong>')
            .then(() => {
                this.requestItemService
                    .deleteItem(request)
                    .subscribe((response) => {
                        this.toastr.success('Request item successfully deleted.');
                        this.refreshList();   
                    });
            }, () => {});
    }

    activate(request: CplRequestBasicData){
        this.requestItemService
            .activateItem(request)
            .subscribe((response) => {
                this.toastr.success('Request item successfully activated.');
                this.refreshList();
            });
    }

    restore(request: CplRequestBasicData){
        this.requestItemService
            .restoreItem(request)
            .subscribe(() => {
                this.toastr.success('Request item successfully restored.');
                this.refreshList();
            });
    }

    refreshList(){
        this.sessionService.Loading(true);
        this.stateService
                .go(this.stateService.$current.name, this.stateService.params, {
                    reload: true
                });
    }

    private generateQuestionnaireSections(questionnaires: CplRequestQfm[], followupQuestionnaires: CplRequestQfm[] ) {
        const requestSection = new DuplicateItemQfmSections({
            title: 'Request Questionnaires',
            questionnaires: questionnaires.map(q => this.generateQuestionnaire(q))
        });

        const followupSection = new DuplicateItemQfmSections({
            title: 'Follow-Up Questionnaires',
            questionnaires: followupQuestionnaires.map(q => this.generateQuestionnaire(q))
        });


        return [requestSection, followupSection];
    }

    private generateQuestionnaire(qfm: CplRequestQfm) {
        const questionnaire = new DuplicateItemQfm({
            QfmTitle: qfm.QfmTitle,
            QfmId: qfm.QfmId.toString(),
            IsTemplate: qfm.IsTemplate
        });

        return questionnaire;
    }

    private generateCplRequestDuplicateData(
        questionnaireSections: CplRequestQfm[][],
        RequestID: string,
        PortalID: number,
        qfmData: DuplicateItemForm
    ) {
        const duplicateData = new CplRequestDuplicateData();
        duplicateData.BasicData = new CplRequestBasicData(RequestID, PortalID, qfmData.title);
        duplicateData.Qfms = qfmData.sections.reduce((qfms: CplRequestQfm[], section: any, sectionIndex: number) => {
            const originalQuestionnaires = questionnaireSections[sectionIndex];
            const newQfms = section.questionnaires.filter((newQfm: DuplicateItemFormQfms) => newQfm.action.toString() !== DuplicateItemActions.REMOVE.toString());

            return qfms.concat(newQfms.map((newQfm: DuplicateItemFormQfms) => {
                const originalQfm = originalQuestionnaires.find(q => q.QfmId.toString() === newQfm.oldQfmId.toString());
                const newCplRequestQfm = Object.assign({}, originalQfm)

                switch(newQfm.action.toString()) {
                    case DuplicateItemActions.DUPLICATE.toString():
                        newCplRequestQfm.QfmId = originalQfm.QfmId;
                        newCplRequestQfm.QfmTitle = newQfm.duplicateQfmTitle;
                        break;
                    case DuplicateItemActions.REPLACE.toString():
                        newCplRequestQfm.QfmId = newQfm.newQfmId;
                        break;
                    case DuplicateItemActions.KEEP.toString():
                    default:
                        newCplRequestQfm.QfmId = originalQfm.QfmId;
                        break;
                }

                return newCplRequestQfm;
            }));
        }, [])

        return duplicateData;
    }
}